<template>
  <div :key="$route.params.id">
    <!-- Main Container -->
    <div class="max-w-6xl mx-auto my-12 px-6">
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-3xl font-bold text-center">
          Competitor Overview Dashboard: <span class="text-violet-700">{{ competitorIncentiveName }}</span>
        </h1>
        <!-- Export Button -->
        <div class="text-right">
          <button @click="exportToPpt" class="bg-violet-700 text-white px-4 py-2 rounded">
            Export to PowerPoint
          </button>
        </div>
      </div>

      <!-- Date Picker -->
      <div id="date-picker" class="bg-white p-6 rounded-lg shadow mb-6">
        <div class="flex justify-between">
          <div>
            <p>FROM: <span class="font-bold">01.01.2024</span></p>
          </div>
          <div>
            <p>UNTIL: <span class="font-bold">{{ new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) }}</span></p>
          </div>
        </div>
      </div>

      <!-- Competitors Ran Promotions and Campaign, Sale, Special -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <!-- Competitors Ran Promotions -->
        <div id="competitors-ran-promotions" class="bg-white pt-10 rounded-lg shadow">
          <p class="text-2xl text-center mb-6">
            Your competitors ran <span class="font-bold text-violet-700">{{ competitorCount }}</span> promotions.
          </p>
        </div>

        <!-- Campaign, Sale, Special Section -->
        <div id="campaign-sale-special" class="bg-white p-6 rounded-lg shadow">
          <div class="flex justify-center">
            <div class="text-center mx-4">
              <p class="font-bold">Campaign</p>
              <p class="font-bold text-violet-700">{{ campaignCount }}</p>
              <p>{{ campaignPercent }}%</p>
            </div>
            <div class="text-center mx-4">
              <p class="font-bold">Sale</p>
              <p class="font-bold text-violet-700">{{ saleCount }}</p>
              <p>{{ salePercent }}%</p>
            </div>
            <div class="text-center mx-4">
              <p class="font-bold">Special</p>
              <p class="font-bold text-violet-700">{{ specialCount }}</p>
              <p>{{ specialPercent }}%</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Type of Incentive and Type of Percentage Promo -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <!-- Type of Incentive -->
        <div id="type-of-incentive" class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-bold mb-4 text-center">Type of Incentive</h2>
          <div class="flex justify-center">
            <div class="text-center mx-4">
              <p class="font-bold">Percentage Promo</p>
              <p class="font-bold text-violet-700">{{ percentCount }}</p>
              <p>{{ percentPercentage }}%</p>
            </div>
            <div class="text-center mx-4">
              <p class="font-bold">Money Promo</p>
              <p class="font-bold text-violet-700">{{ euroCount }}</p>
              <p>{{ euroPercentage }}%</p>
            </div>
            <div class="text-center mx-4">
              <p class="font-bold">Others</p>
              <p class="font-bold text-violet-700">{{ noneCount }}</p>
              <p>{{ nonePercentage }}%</p>
            </div>
          </div>
        </div>

        <!-- Type of Percentage Promo -->
        <div id="type-of-percentage-promo" class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-bold mb-4 text-center">Type of Percentage Promo</h2>
          <div class="flex justify-center">
            <div class="text-center mx-4">
              <p class="font-bold">Flat Percentage</p>
              <p class="font-bold text-violet-700">{{ fixedPercentageCount }}</p>
              <p>{{ fixedPercentageShare }}%</p>
            </div>
            <div class="text-center mx-4">
              <p class="font-bold">"Bis zu" Percentage</p>
              <p class="font-bold text-violet-700">{{ bisZuPercentageCount }}</p>
              <p>{{ bisZuPercentageShare }}%</p>
            </div>
            <div class="text-center mx-4">
              <p class="font-bold">Others</p>
              <p class="font-bold text-violet-700">{{ othersCount }}</p>
              <p>{{ othersShare }}%</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Promo Emails -->
      <div id="promo-emails" class="bg-white p-6 rounded-lg shadow mb-6">
        <h2 class="text-xl font-bold mb-4 text-center">Promo Emails</h2>
        <div class="flex justify-center">
          <div class="text-center mx-4">
            <p class="font-bold">Sent</p>
            <p class="font-bold"><span class="text-violet-700">{{ totalEntries }}</span> emails</p>
          </div>
          <div class="text-center mx-4">
            <p class="font-bold">AVG per Promotion</p>
            <p class="font-bold"><span class="text-violet-700">{{ averageAktionKeyCount.toFixed(1) }}</span> emails</p>
          </div>
          <div class="text-center mx-4">
            <p class="font-bold">AVG Runtimes per Promotion</p>
            <p class="font-bold"><span class="text-violet-700">{{ averageRuntimeDays.toFixed(1) }}</span> days</p>
          </div>
        </div>
      </div>

      <!-- Distribution of Incentives -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <!-- Distribution of Incentives (Percentage Promos) -->
        <div id="distribution-incentives" class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-bold mb-4 text-center">
            Distribution of Incentives (Fix Percentage Promos)
          </h2>
          <p class="text-center mb-4">
            ø Voucher Height: <span class="font-bold text-violet-700">{{ avgVoucherHeightFixed }}%</span>
          </p>
          <div>
            <canvas id="distributionIncentivesChart"></canvas>
          </div>
          <table class="w-full text-left border-collapse border border-gray-300 mt-4">
            <thead>
              <tr>
                <th class="border border-gray-300 p-2">Incentive</th>
                <th class="border border-gray-300 p-2">Distribution (%)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in discountDistributionData" :key="index">
                <td class="border border-gray-300 p-2">{{ item.rabatthoehe }}</td>
                <td class="border border-gray-300 p-2">{{ item.distribution_percentage.toFixed(2) }}%</td>
              </tr>
              <tr>
                <td class="border border-gray-300 p-2 font-bold">Total</td>
                <td class="border border-gray-300 p-2 font-bold">
                  {{ discountDistributionData.reduce((acc, item) => acc + item.distribution_percentage, 0).toFixed(2) }}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Distribution of Incentives (Bis zu Percentage Promos) -->
        <div id="distribution-bis-zu-incentives" class="bg-white p-6 rounded-lg shadow">
          <h2 class="text-xl font-bold mb-4 text-center">
            Distribution of Incentives ("Bis zu" Promos)
          </h2>
          <p class="text-center mb-4">
            ø Voucher Height: <span class="font-bold text-violet-700">{{ avgVoucherHeightBisZu }}%</span>
          </p>
          <div>
            <canvas id="distributionBisZuIncentivesChart"></canvas>
          </div>
          <table class="w-full text-left border-collapse border border-gray-300 mt-4">
            <thead>
              <tr>
                <th class="border border-gray-300 p-2">Incentive</th>
                <th class="border border-gray-300 p-2">Distribution (%)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in discountDistributionBisZuData" :key="index">
                <td class="border border-gray-300 p-2">{{ item.rabatthoehe }}</td>
                <td class="border border-gray-300 p-2">{{ item.distribution_percentage.toFixed(2) }}%</td>
              </tr>
              <tr>
                <td class="border border-gray-300 p-2 font-bold">Total</td>
                <td class="border border-gray-300 p-2 font-bold">
                  {{ discountDistributionBisZuData.reduce((acc, item) => acc + item.distribution_percentage, 0).toFixed(2) }}%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Pie Charts -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <!-- Minimum Order Value vs No MOV -->
        <div id="mov-vs-no-mov" class="bg-white p-6 rounded-lg shadow text-center">
          <h2 class="text-xl font-bold mb-4">Minimum Order Value vs. No MOV</h2>
          <canvas id="movChart"></canvas>
        </div>
        <!-- Voucher Code vs No Voucher Code -->
        <div id="voucher-code-vs-no-voucher-code" class="bg-white p-6 rounded-lg shadow text-center">
          <h2 class="text-xl font-bold mb-4">Voucher Code vs. No Voucher Code</h2>
          <canvas id="voucherCodeChart"></canvas>
        </div>
        <!-- EXTRA Discount vs No EXTRA Discount -->
        <div id="extra-discount-vs-no-extra-discount" class="bg-white p-6 rounded-lg shadow text-center">
          <h2 class="text-xl font-bold mb-4">
            EXTRA Discount vs. No EXTRA Discount
          </h2>
          <canvas id="extraDiscountChart"></canvas>
        </div>
      </div>

      <!-- Category Focus Promo Emails -->
      <div id="category-focus-promo-emails" class="bg-white p-6 rounded-lg shadow mb-6" v-if="showCategoryChart">
        <h2 class="text-xl font-bold mb-4 text-center">
          Category Focus Promo Emails
        </h2>
        <div>
          <canvas id="categoryFocusPromoEmailsChart"></canvas>
        </div>
      </div>

      <!-- Subcategory Focus (Promo Emails) -->
      <div id="subcategory-focus-promo-emails" class="mb-6">
        <h2 class="text-xl font-bold mb-4 text-center">
          Subcategory Focus (Promo Emails)
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
  <!-- Accessoires Chart -->
  <div v-if="showAccessoiresChart" class="bg-white p-6 rounded-lg shadow text-center">
    <h3 class="text-lg font-bold mb-4">Accessoires</h3>
    <canvas id="accessoiresChart"></canvas>
  </div>

  <!-- Bekleidung Chart -->
  <div v-if="showBekleidungChart" class="bg-white p-6 rounded-lg shadow text-center">
    <h3 class="text-lg font-bold mb-4">Bekleidung</h3>
    <canvas id="bekleidungChart"></canvas>
  </div>

  <!-- Marken Chart -->
  <div v-if="showMarkenChart" class="bg-white p-6 rounded-lg shadow text-center">
    <h3 class="text-lg font-bold mb-4">Marken</h3>
    <canvas id="markenChart"></canvas>
  </div>

  <!-- Schuhe Chart -->
  <div v-if="showSchuheChart" class="bg-white p-6 rounded-lg shadow text-center">
    <h3 class="text-lg font-bold mb-4">Schuhe</h3>
    <canvas id="schuheChart"></canvas>
  </div>

  <!-- Verschiedenes Chart -->
  <div v-if="showVerschiedenesChart" class="bg-white p-6 rounded-lg shadow text-center">
    <h3 class="text-lg font-bold mb-4">Verschiedenes</h3>
    <canvas id="verschiedenesChart"></canvas>
  </div>

  <!-- Sonstiges Chart -->
  <div v-if="showSonstigesChart" class="bg-white p-6 rounded-lg shadow text-center">
    <h3 class="text-lg font-bold mb-4">Sonstiges</h3>
    <canvas id="sonstigesChart"></canvas>
  </div>
</div>

      </div>

    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick, computed } from "vue";
import { useRoute } from "vue-router";
import { useDynamicDashboardStore } from '@/stores/useDynamicDashboardStore';
import { Chart, registerables } from "chart.js";
import PptxGenJS from "pptxgenjs";
import html2canvas from "html2canvas";

export default {
  name: "CompetitorDashboard",
  setup() {
    const route = useRoute();
    const id = ref(route.params.id);
    const store = useDynamicDashboardStore();
    store.setIdFromRoute();

    watch(
      () => route.params.id,
      async (newId) => {
        id.value = newId;
        store.id = newId;
        await fetchAllData();
      }
    );

    const fetchAllData = async () => {
      await store.fetchCompetitorCount();
      await store.fetchActionTypeCounts();
      await store.fetchCompetitorIncentive();
      await store.fetchRabatthoehe();
      await store.fetchAverageRuntimeDays();
      await store.fetchDiscountDistribution();
      await store.fetchDiscountDistributionBisZu();
      await store.fetchMovStatusPercentage();
      await store.fetchVoucherCodeStatusPercentage();
      await store.fetchIncentivierungExtraCounts();

      // Fetch new category endpoints
      await store.fetchCategoryCounts();
      await store.fetchAccessoiresSortimentCounts();
      await store.fetchBekleidungSortimentCounts();
      await store.fetchMarkenSortimentCounts();
      await store.fetchSchuheSortimentCounts();
      await store.fetchVerschiedenesSortimentCounts();
      await store.fetchSonstigesSortimentCounts();
    };

    const competitorCount = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/count');
      return data.entry_count || 0;
    });

    const actionTypeCounts = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/action-type-counts');
      if (!Array.isArray(data)) return {Aktion: 0, Sale: 0, Special: 0};

      const counts = {Aktion: 0, Sale: 0, Special: 0};
      data.forEach(item => {
        if (item.aktions_typ in counts) {
          counts[item.aktions_typ] = item.count;
        }
      });
      return counts;
    });

    const totalPromotions = computed(() =>
      Object.values(actionTypeCounts.value).reduce((sum, val) => sum + val, 0)
    );

    const campaignCount = computed(() => actionTypeCounts.value['Aktion'] || 0);
    const saleCount = computed(() => actionTypeCounts.value['Sale'] || 0);
    const specialCount = computed(() => actionTypeCounts.value['Special'] || 0);

    const campaignPercent = computed(() =>
      totalPromotions.value > 0 ? ((campaignCount.value / totalPromotions.value) * 100).toFixed(0) : 0
    );
    const salePercent = computed(() =>
      totalPromotions.value > 0 ? ((saleCount.value / totalPromotions.value) * 100).toFixed(0) : 0
    );
    const specialPercent = computed(() =>
      totalPromotions.value > 0 ? ((specialCount.value / totalPromotions.value) * 100).toFixed(0) : 0
    );

    const competitorIncentive = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/incentive');
      return Array.isArray(data) && data.length > 0 ? data[0] : {};
    });

    const competitorIncentiveName = computed(() => competitorIncentive.value.name || '');
    const percentCount = computed(() => competitorIncentive.value.percent_count || 0);
    const euroCount = computed(() => competitorIncentive.value.euro_count || 0);
    const noneCount = computed(() => competitorIncentive.value.none_count || 0);
    const percentPercentage = computed(() => competitorIncentive.value.percent_percentage || 0);
    const euroPercentage = computed(() => competitorIncentive.value.euro_percentage || 0);
    const nonePercentage = computed(() => competitorIncentive.value.none_percentage || 0);

    const rabatthoeheData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/rabatthoehe');
      return Array.isArray(data) ? data : [];
    });

    const bisZuPercentageCount = computed(() => {
      const item = rabatthoeheData.value.find(x => x.category === '"bis zu" Percentage');
      return item ? item.entry_count : 0;
    });

    const fixedPercentageCount = computed(() => {
      const item = rabatthoeheData.value.find(x => x.category === 'Fixed Percentage');
      return item ? item.entry_count : 0;
    });

    const othersCount = computed(() => {
      const item = rabatthoeheData.value.find(x => x.category === 'Others');
      return item ? item.entry_count : 0;
    });

    const totalRabatthoehe = computed(() => {
      return bisZuPercentageCount.value + fixedPercentageCount.value + othersCount.value;
    });

    const bisZuPercentageShare = computed(() =>
      totalRabatthoehe.value > 0 ? ((bisZuPercentageCount.value / totalRabatthoehe.value) * 100).toFixed(0) : 0
    );
    const fixedPercentageShare = computed(() =>
      totalRabatthoehe.value > 0 ? ((fixedPercentageCount.value / totalRabatthoehe.value) * 100).toFixed(0) : 0
    );
    const othersShare = computed(() =>
      totalRabatthoehe.value > 0 ? ((othersCount.value / totalRabatthoehe.value) * 100).toFixed(0) : 0
    );

    const averageRuntimeData = computed(() => store.getEndpointData('competitor-detail/wettbewerber/average-runtime-days'));
    const averageRuntimeArray = computed(() => {
      return averageRuntimeData.value && Array.isArray(averageRuntimeData.value.average_runtime_days)
        ? averageRuntimeData.value.average_runtime_days
        : [];
    });
    const totalEntries = computed(() => averageRuntimeArray.value.length > 0 ? averageRuntimeArray.value[0].total_entries : 0);
    const averageAktionKeyCount = computed(() => averageRuntimeArray.value.length > 0 ? averageRuntimeArray.value[0].average_aktion_key_count : 0);
    const averageRuntimeDays = computed(() => averageRuntimeArray.value.length > 0 ? averageRuntimeArray.value[0].average_runtime_days : 0);

    const discountDistributionData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/discount-distribution');
      return Array.isArray(data) ? data : [];
    });

    const discountDistributionBisZuData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/discount-distribution-bis-zu');
      return Array.isArray(data) ? data : [];
    });

    const avgVoucherHeightFixed = computed(() => {
      if (!discountDistributionData.value.length) return 0;
      const sum = discountDistributionData.value.reduce((acc, item) => {
        const val = parseFloat(item.rabatthoehe);
        return acc + (isNaN(val) ? 0 : val);
      }, 0);
      return (sum / discountDistributionData.value.length).toFixed(2);
    });

    const avgVoucherHeightBisZu = computed(() => {
      if (!discountDistributionBisZuData.value.length) return 0;
      const sum = discountDistributionBisZuData.value.reduce((acc, item) => {
        const val = parseFloat(item.rabatthoehe.replace('bis zu ', '').replace('%', ''));
        return acc + (isNaN(val) ? 0 : val);
      }, 0);
      return (sum / discountDistributionBisZuData.value.length).toFixed(2);
    });

    const distributionIncentivesChartData = computed(() => {
      const labels = discountDistributionData.value.map(item => item.rabatthoehe);
      const dataPoints = discountDistributionData.value.map(item => item.distribution_percentage);

      return {
        labels,
        datasets: [
          {
            label: "Incentive Distribution",
            data: dataPoints,
            backgroundColor: '#9333ea',
            borderWidth: 0,
            borderRadius: 10,
            barThickness: 20,
          },
        ],
        options: { responsive: true, plugins: {
      legend: {
        display: false, // Legende wird ausgeblendet
      },
    }, },
      };
    });

    const distributionBisZuIncentivesChartData = computed(() => {
      const labels = discountDistributionBisZuData.value.map(item => item.rabatthoehe);
      const dataPoints = discountDistributionBisZuData.value.map(item => item.distribution_percentage);

      return {
        labels,
        datasets: [
          {
            label: "Incentive Distribution",
            data: dataPoints,
            backgroundColor: '#9333ea',
            borderWidth: 0,
            borderRadius: 10,
            barThickness: 20,
          },
        ],
        options: { responsive: true, plugins: {
      legend: {
        display: false, // Legende wird ausgeblendet
      },
    }, },
      };
    });

    // Pie charts data
    const movStatusData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/mov-status-percentage');
      return Array.isArray(data) ? data : [];
    });

    const movChartData = computed(() => {
      const labels = movStatusData.value.map(item => item.mov_status);
      const dataPoints = movStatusData.value.map(item => item.percentage);

      return {
        labels,
        datasets: [
          {
            data: dataPoints,
            backgroundColor: ['#f87171', '#9333ea'],
            borderWidth: 6,
            borderRadius: 10,
          },
        ],
      };
    });

    const voucherStatusData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/voucher-code-status-percentage');
      return Array.isArray(data) ? data : [];
    });

    const voucherCodeChartData = computed(() => {
      const labels = voucherStatusData.value.map(item => item.voucher_status);
      const dataPoints = voucherStatusData.value.map(item => item.percentage);

      return {
        labels,
        datasets: [
          {
            data: dataPoints,
            backgroundColor: ['#f87171', '#9333ea'],
            borderWidth: 6,
            borderRadius: 10,
          },
        ],
      };
    });

    const extraData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/incentivierung-extra-counts');
      return Array.isArray(data) && data.length > 0 ? data[0] : {};
    });

    const extraDiscountChartData = computed(() => {
      const labels = ["EXTRA Discount", "No EXTRA Discount"];
      const dataPoints = [extraData.value.extra_percentage || 0, extraData.value.no_extra_percentage || 0];

      return {
        labels,
        datasets: [
          {
            data: dataPoints,
            backgroundColor: ['#f87171', '#9333ea'],
            borderWidth: 6,
            borderRadius: 10,
          },
        ],
      };
    });

    // Category and Subcategory data
    const categoryCounts = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/category-counts');
      return Array.isArray(data) ? data : [];
    });

    const showCategoryChart = computed(() => categoryCounts.value.length > 0);

    const categoryFocusChartData = computed(() => {
      if (!showCategoryChart.value) return null;
      const labels = categoryCounts.value.map(item => item.category);
      const dataPoints = categoryCounts.value.map(item => item.count);
      return {
        labels,
        datasets: [
          {
            label: "Promo Emails",
            data: dataPoints,
            backgroundColor: '#9333ea',
            borderWidth: 0,
            borderRadius: 10,
            barThickness: 20,
          },
        ],
      };
    });

    // Accessoires
    const accessoiresData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/accessoires-sortiment-counts');
      return Array.isArray(data) ? data : [];
    });
    const showAccessoiresChart = computed(() => accessoiresData.value.length > 0);
    const accessoiresChartData = computed(() => {
      if(!showAccessoiresChart.value) return null;
      const labels = accessoiresData.value.map(item => item.unique_sortiment);
      const dataPoints = accessoiresData.value.map(item => item.count);
      return {
        labels,
        datasets: [{
          label: "Accessoires",
          data: dataPoints,
          backgroundColor: '#9333ea',
          borderWidth: 0,
            borderRadius: 10,
            barThickness: 20,
        }]
      };
    });

    // Bekleidung
    const bekleidungData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/bekleidung-sortiment-counts');
      return Array.isArray(data) ? data : [];
    });
    const showBekleidungChart = computed(() => bekleidungData.value.length > 0);
    const bekleidungChartData = computed(() => {
      if(!showBekleidungChart.value) return null;
      const labels = bekleidungData.value.map(item => item.unique_sortiment);
      const dataPoints = bekleidungData.value.map(item => item.count);
      return {
        labels,
        datasets: [{
          label: "Bekleidung",
          data: dataPoints,
          backgroundColor: '#9333ea',
          borderWidth: 0,
            borderRadius: 10,
            barThickness: 20,
        }]
      };
    });

    // Marken
    const markenData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/marken-sortiment-counts');
      return Array.isArray(data) ? data : [];
    });
    const showMarkenChart = computed(() => markenData.value.length > 0);
    const markenChartData = computed(() => {
      if(!showMarkenChart.value) return null;
      const labels = markenData.value.map(item => item.unique_sortiment);
      const dataPoints = markenData.value.map(item => item.count);
      return {
        labels,
        datasets: [{
          label: "Marken",
          data: dataPoints,
          backgroundColor: '#9333ea',
          borderWidth: 0,
            borderRadius: 10,
            barThickness: 20,
        }]
      };
    });

    // Schuhe
    const schuheData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/schuhe-sortiment-counts');
      return Array.isArray(data) ? data : [];
    });
    const showSchuheChart = computed(() => schuheData.value.length > 0);
    const schuheChartData = computed(() => {
      if(!showSchuheChart.value) return null;
      const labels = schuheData.value.map(item => item.unique_sortiment);
      const dataPoints = schuheData.value.map(item => item.count);
      return {
        labels,
        datasets: [{
          label: "Schuhe",
          data: dataPoints,
          backgroundColor: '#9333ea',
          borderWidth: 0,
            borderRadius: 10,
            barThickness: 20,
        }]
      };
    });

    // Verschiedenes
    const verschiedenesData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/verschiedenes-sortiment-counts');
      return Array.isArray(data) ? data : [];
    });
    const showVerschiedenesChart = computed(() => verschiedenesData.value.length > 0);
    const verschiedenesChartData = computed(() => {
      if(!showVerschiedenesChart.value) return null;
      const labels = verschiedenesData.value.map(item => item.unique_sortiment);
      const dataPoints = verschiedenesData.value.map(item => item.count);
      return {
        labels,
        datasets: [{
          label: "Verschiedenes",
          data: dataPoints,
          backgroundColor: '#9333ea',
          borderWidth: 0,
            borderRadius: 10,
            barThickness: 20,
        }]
      };
    });

    // Sonstiges
    const sonstigesData = computed(() => {
      const data = store.getEndpointData('competitor-detail/wettbewerber/sonstiges-sortiment-counts');
      return Array.isArray(data) ? data : [];
    });
    const showSonstigesChart = computed(() => sonstigesData.value.length > 0);
    const sonstigesChartData = computed(() => {
      if(!showSonstigesChart.value) return null;
      const labels = sonstigesData.value.map(item => item.unique_sortiment);
      const dataPoints = sonstigesData.value.map(item => item.count);
      return {
        labels,
        datasets: [{
          label: "Sonstiges",
          data: dataPoints,
          backgroundColor: '#9333ea',
            borderWidth: 0,
            borderRadius: 10,
            barThickness: 20,
        }]
      };
    });

    const exportToPpt = async () => {
      let pptx = new PptxGenJS();
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, "0");
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const yyyy = today.getFullYear();
      const todayStr = `${dd}.${mm}.${yyyy}`;

      let slide = pptx.addSlide();
      slide.addText(`competify competitor monitoring from ${todayStr}`, {
        x: 1,
        y: 3,
        fontSize: 24,
        bold: true,
        align: "center",
        w: "80%",
      });

      await nextTick();

      const getCanvasImage = (canvasId) => {
        const canvas = document.getElementById(canvasId);
        return canvas ? canvas.toDataURL("image/png") : null;
      };

      const getElementImage = async (elementId) => {
        const element = document.getElementById(elementId);
        if (!element) return null;
        const canvas = await html2canvas(element);
        return canvas.toDataURL("image/png");
      };

      const areas = [
        { id: "date-picker", title: "Date Picker" },
        { id: "competitors-ran-promotions", title: "Competitors Ran Promotions" },
        { id: "campaign-sale-special", title: "Campaign, Sale, Special" },
        { id: "type-of-incentive", title: "Type of Incentive" },
        { id: "type-of-percentage-promo", title: "Type of Percentage Promo" },
        { id: "promo-emails", title: "Promo Emails" },
        { id: "distribution-incentives", title: "Distribution of Incentives (Percentage Promos)" },
        { id: "distribution-bis-zu-incentives", title: "Distribution of Incentives (Bis zu Percentage Promos)" },
      ];

      for (let area of areas) {
        let imageData = await getElementImage(area.id);
        if (!imageData) continue;
        slide = pptx.addSlide();
        slide.addImage({ data: imageData, x: 0.5, y: 1, w: 9, h: 5 });
        slide.addText(area.title, { x: 0.5, y: 0.5, fontSize: 18, bold: true });
      }

      const charts = [
        {
          id: "distributionIncentivesChart",
          title: "Distribution of Incentives (Percentage Promos)",
        },
        {
          id: "distributionBisZuIncentivesChart",
          title: "Distribution of Incentives (Bis zu Percentage Promos)",
        },
        {
          id: "movChart",
          title: "Minimum Order Value vs. No MOV",
        },
        {
          id: "voucherCodeChart",
          title: "Voucher Code vs. No Voucher Code",
        },
        {
          id: "extraDiscountChart",
          title: "EXTRA Discount vs. No EXTRA Discount",
        },
        {
          id: "categoryFocusPromoEmailsChart",
          title: "Category Focus Promo Emails",
        },
        {
          id: "schuheSubcategoryChart",
          title: "Subcategory Focus - Schuhe",
        },
        {
          id: "variousSubcategoryChart",
          title: "Subcategory Focus - Verschiedenes",
        },
      ];

      for (let chart of charts) {
        let imageData = getCanvasImage(chart.id);
        if (!imageData) continue;
        slide = pptx.addSlide();
        slide.addImage({ data: imageData, x: 0.5, y: 1, w: 9, h: 5 });
        slide.addText(chart.title, { x: 0.5, y: 0.5, fontSize: 18, bold: true });
      }

      pptx.writeFile(`competitor_dashboard_${todayStr}.pptx`);
    };

    onMounted(async () => {
      Chart.register(...registerables);
      await fetchAllData();

      // Initialize charts conditionally
      if (distributionIncentivesChartData.value.labels && distributionIncentivesChartData.value.labels.length > 0) {
        const distributionIncentivesCtx = document.getElementById("distributionIncentivesChart").getContext("2d");
        new Chart(distributionIncentivesCtx, {
          type: "bar",
          data: distributionIncentivesChartData.value,
          options: { responsive: true, plugins: {
      legend: {
        display: false, // Legende wird ausgeblendet
      },
    },  },
        });
      }

      if (distributionBisZuIncentivesChartData.value.labels && distributionBisZuIncentivesChartData.value.labels.length > 0) {
        const distributionBisZuIncentivesCtx = document.getElementById("distributionBisZuIncentivesChart").getContext("2d");
        new Chart(distributionBisZuIncentivesCtx, {
          type: "bar",
          data: distributionBisZuIncentivesChartData.value,
          options: { responsive: true, plugins: {
      legend: {
        display: false, // Legende wird ausgeblendet
      },
    },  },
        });
      }

      if (movChartData.value.labels && movChartData.value.labels.length > 0) {
        const movCtx = document.getElementById("movChart").getContext("2d");
        new Chart(movCtx, {
          type: "pie",
          data: movChartData.value,
          options: { responsive: true },
        });
      }

      if (voucherCodeChartData.value.labels && voucherCodeChartData.value.labels.length > 0) {
        const voucherCodeCtx = document.getElementById("voucherCodeChart").getContext("2d");
        new Chart(voucherCodeCtx, {
          type: "pie",
          data: voucherCodeChartData.value,
          options: { responsive: true },
        });
      }

      if (extraDiscountChartData.value.labels && extraDiscountChartData.value.labels.length > 0) {
        const extraDiscountCtx = document.getElementById("extraDiscountChart").getContext("2d");
        new Chart(extraDiscountCtx, {
          type: "pie",
          data: extraDiscountChartData.value,
          options: { responsive: true },
        });
      }

      if (showCategoryChart.value) {
        const categoryFocusCtx = document.getElementById("categoryFocusPromoEmailsChart").getContext("2d");
        new Chart(categoryFocusCtx, {
          type: "bar",
          data: categoryFocusChartData.value,
          options: { responsive: true, plugins: {
      legend: {
        display: false, // Legende wird ausgeblendet
      },
    }, },
        });
      }

      if (showAccessoiresChart.value) {
        const accessoiresCtx = document.getElementById("accessoiresChart").getContext("2d");
        new Chart(accessoiresCtx, {
          type: "bar",
          data: accessoiresChartData.value,
          options: { responsive: true, plugins: {
      legend: {
        display: false, // Legende wird ausgeblendet
      },
    }, },
        });
      }

      if (showBekleidungChart.value) {
        const bekleidungCtx = document.getElementById("bekleidungChart").getContext("2d");
        new Chart(bekleidungCtx, {
          type: "bar",
          data: bekleidungChartData.value,
          options: { responsive: true, plugins: {
      legend: {
        display: false, // Legende wird ausgeblendet
      },
    }, },
        });
      }

      if (showMarkenChart.value) {
        const markenCtx = document.getElementById("markenChart").getContext("2d");
        new Chart(markenCtx, {
          type: "bar",
          data: markenChartData.value,
          options: { responsive: true, plugins: {
      legend: {
        display: false, // Legende wird ausgeblendet
      },
    }, },
        });
      }

      if (showSchuheChart.value) {
        const schuheCtx = document.getElementById("schuheChart").getContext("2d");
        new Chart(schuheCtx, {
          type: "bar",
          data: schuheChartData.value,
          options: { responsive: true, plugins: {
      legend: {
        display: false, // Legende wird ausgeblendet
      },
    }, },
        });
      }

      if (showVerschiedenesChart.value) {
        const verschiedenesCtx = document.getElementById("verschiedenesChart").getContext("2d");
        new Chart(verschiedenesCtx, {
          type: "bar",
          data: verschiedenesChartData.value,
          options: { responsive: true, plugins: {
      legend: {
        display: false, // Legende wird ausgeblendet
      },
    }, },
        });
      }

      if (showSonstigesChart.value) {
        const sonstigesCtx = document.getElementById("sonstigesChart").getContext("2d");
        new Chart(sonstigesCtx, {
          type: "bar",
          data: sonstigesChartData.value,
          options: { responsive: true, plugins: {
      legend: {
        display: false, // Legende wird ausgeblendet
      },
    }, },
        });
      }
    });

    return {
      id,
      exportToPpt,
      competitorCount,
      campaignCount,
      saleCount,
      specialCount,
      campaignPercent,
      salePercent,
      specialPercent,
      competitorIncentiveName,
      percentCount,
      euroCount,
      noneCount,
      percentPercentage,
      euroPercentage,
      nonePercentage,
      bisZuPercentageCount,
      fixedPercentageCount,
      othersCount,
      bisZuPercentageShare,
      fixedPercentageShare,
      othersShare,
      totalEntries,
      averageAktionKeyCount,
      averageRuntimeDays,
      discountDistributionData,
      discountDistributionBisZuData,
      avgVoucherHeightFixed,
      avgVoucherHeightBisZu,
      distributionIncentivesChartData,
      distributionBisZuIncentivesChartData,
      movChartData,
      voucherCodeChartData,
      extraDiscountChartData,

      // Category & Subcategories
      showCategoryChart,
      showAccessoiresChart,
      showBekleidungChart,
      showMarkenChart,
      showSchuheChart,
      showVerschiedenesChart,
      showSonstigesChart,
    };
  },
};
</script>

<style scoped>
.id-display {
  background-color: #f9f9f9;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 1.1rem;
}
.table-header {
  background-color: #f3f4f6;
  font-weight: bold;
}
</style>
